import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { cercaOrdine, OrdineTrovato, TipoOrdine } from "../../api/ordiniOdoo";
import { OrdineCollegato, OrdiniCollegati } from "../../types";
import { Input } from "./Input";

/**
 * I cinque campi OdA/OdV — un componente solo, usato dai quattro moduli.
 *
 * ## Perche' un componente e non cinque campi copiati quattro volte
 *
 * Le colonne sono identiche su installazioni, attivazioni, interventi e
 * sopralluoghi, e il report amministrativo (`/reports/ordini`) le legge da tutte
 * e quattro mettendole nello stesso elenco. Quattro copie dell'interfaccia
 * vorrebbero dire quattro etichette che divergono alla prima correzione, e un
 * elenco in cui la stessa colonna si chiama in due modi a seconda di dove e'
 * stata compilata. Stesso motivo per cui esistono `VigilanzaCongiunta` e
 * `CampiOrario`.
 *
 * ## Perche' `odv_in_garanzia` e' una tendina e non una spunta
 *
 * Perche' ha TRE valori e una spunta ne ha due. «Non specificato» non e' «No»:
 * e' la sezione "Da qualificare" del report, cioe' la domanda ancora aperta. Una
 * casella non spuntata avrebbe detto «va fatturato» su ogni riga che nessuno ha
 * ancora guardato. Stessa scelta, per la stessa ragione, dei `YN` a tre stati del
 * form Sopralluoghi.
 *
 * ## Chi lo vede
 *
 * Lo decide il chiamante, e in tutti e quattro i casi la condizione e' la stessa:
 * solo admin e staff. Il riquadro non e' nascosto per gerarchia ma per
 * CONTENUTO — il numero d'ordine al fornitore e lo stato della fattura al cliente
 * sono dati commerciali di Evo, e su questi moduli lavorano anche Centri
 * Assistenza, Istituti di Vigilanza e (sui sopralluoghi) utenti cliente. Il
 * backend applica lo stesso limite e ignora i cinque campi per chiunque altro:
 * questa non e' la guardia, e' l'interfaccia che non offre cio' che non passerebbe.
 *
 * ## Un ordine puo' coprire piu' attivita' (issue #211, PR A)
 *
 * Il numero non e' piu' un testo del record: identifica un ordine
 * (`ordini_odoo`), e scrivere un numero che esiste gia' COLLEGA questa attivita'
 * a quell'ordine. Fattura e garanzia sono dell'ordine, quindi una spunta vale
 * per tutte le attivita' che copre. Il riquadro lo dice prima del salvataggio:
 * quante altre attivita' copre l'ordine collegato, e se il numero appena
 * scritto e' di un ordine esistente (con la sua fattura e la sua garanzia, che
 * il riquadro carica al posto di quelle che c'erano) o di uno nuovo.
 */

/**
 * Chi puo' vedere e scrivere i cinque campi: admin e staff, nessun altro.
 *
 * Un hook e non cinque controlli di ruolo copiati nei quattro form (piu' il
 * pannello di modifica degli interventi): la regola e' una, e scritta cinque
 * volte la prima correzione ne aggiorna quattro. E' la faccia frontend del
 * `if (scope.isAdmin)` che nelle otto rotte decide la stessa cosa — se cambia
 * una delle due, vanno cambiate entrambe.
 *
 * Non e' una guardia: il backend ignora i cinque campi per chiunque altro. Qui si
 * evita solo di offrire un riquadro che non salverebbe niente.
 */
export function usaPuoGestireRiferimentiOdoo(): boolean {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "staff";
}

/** Le tre spunte dell'ordine: fattura OdA, garanzia OdV, fattura OdV. */
export type SpuntaOrdine = "oda_fattura_ricevuta" | "odv_in_garanzia" | "odv_fatturato";
const SPUNTE: readonly SpuntaOrdine[] = ["oda_fattura_ricevuta", "odv_in_garanzia", "odv_fatturato"];

/**
 * I cinque valori nella forma del FORM, non del database, piu' quali spunte ha
 * toccato l'utente.
 *
 * `odv_in_garanzia` e' una stringa e non `boolean | null` perche' e' il valore di
 * un `<select>`: `""` (non specificato) | `"true"` | `"false"`. La traduzione
 * verso il database sta in `payloadRiferimentiOdoo`, in un posto solo.
 *
 * `spunte_toccate` (review della PR #214): le spunte si riempiono anche da sole,
 * con i valori dell'ordine a cui porta il numero scritto. Quelle NON sono
 * modifiche dell'utente e non devono partire: rimandarle sovrascriverebbe
 * l'ordine con una fotografia presa all'apertura del form, magari mentre un'altra
 * attivita' dello stesso ordine lo aggiornava. Partono solo le spunte toccate a
 * mano, e la ricerca del numero non le cambia mai. Non e' un campo dell'API:
 * `senzaRiferimentiOdoo` lo toglie insieme agli altri cinque.
 */
export interface ValoriRiferimentiOdoo {
  numero_oda: string;
  oda_fattura_ricevuta: boolean;
  numero_odv: string;
  odv_in_garanzia: string;
  odv_fatturato: boolean;
  spunte_toccate: Partial<Record<SpuntaOrdine, true>>;
}

/** I cinque campi come arrivano dal backend. */
interface RecordConRiferimenti {
  numero_oda?: string | null;
  oda_fattura_ricevuta?: boolean | null;
  numero_odv?: string | null;
  odv_in_garanzia?: boolean | null;
  odv_fatturato?: boolean | null;
}

/** I valori vuoti, per il form di creazione. */
export const RIFERIMENTI_ODOO_VUOTI: ValoriRiferimentiOdoo = {
  numero_oda: "",
  oda_fattura_ricevuta: false,
  numero_odv: "",
  odv_in_garanzia: "",
  odv_fatturato: false,
  spunte_toccate: {},
};

/**
 * Dal record ai valori del form. `undefined` e `null` finiscono entrambi sul
 * valore vuoto: il primo capita quando il campo non e' stato mandato affatto (un
 * endpoint che non lo seleziona), il secondo quando in tabella e' nullo, e per il
 * form sono la stessa cosa — niente da mostrare.
 */
export function riferimentiOdooDaRecord(record?: RecordConRiferimenti | null): ValoriRiferimentiOdoo {
  if (!record) return { ...RIFERIMENTI_ODOO_VUOTI };
  return {
    numero_oda: record.numero_oda ?? "",
    oda_fattura_ricevuta: record.oda_fattura_ricevuta ?? false,
    numero_odv: record.numero_odv ?? "",
    // `== null` e non `?? ""`: `false` e' un valore da conservare, e con `??`
    // sarebbe passato ma con un `||` si sarebbe perso. Scritto esplicito perche'
    // e' proprio il punto in cui si sbaglia.
    odv_in_garanzia: record.odv_in_garanzia == null ? "" : String(record.odv_in_garanzia),
    odv_fatturato: record.odv_fatturato ?? false,
    spunte_toccate: {},
  };
}

/** I cinque campi nella forma del backend. */
export interface PayloadRiferimentiOdoo {
  numero_oda: string | null;
  oda_fattura_ricevuta: boolean;
  numero_odv: string | null;
  odv_in_garanzia: boolean | null;
  odv_fatturato: boolean;
}

/** Dai valori del form alla forma del backend, tutti e cinque. */
function normalizza(v: ValoriRiferimentiOdoo): PayloadRiferimentiOdoo {
  return {
    numero_oda: v.numero_oda.trim() || null,
    oda_fattura_ricevuta: v.oda_fattura_ricevuta,
    numero_odv: v.numero_odv.trim() || null,
    odv_in_garanzia: v.odv_in_garanzia === "" ? null : v.odv_in_garanzia === "true",
    odv_fatturato: v.odv_fatturato,
  };
}

/**
 * Dai valori del form al payload per il backend: i NUMERI solo se cambiati
 * rispetto a `iniziali`, le SPUNTE solo se l'utente le ha toccate.
 *
 * E' la faccia frontend di `riferimentiOdooDalBody` in
 * `backend/src/utils/riferimentiOdoo.ts`, e dice la stessa cosa: un numero vuoto
 * e' `null` e non `""`, e `odv_in_garanzia` non specificato e' `null` e non
 * `false`. Il backend rinormalizza comunque — e' lui il controllo vero.
 *
 * Solo i cambiati, e non piu' tutti e cinque, dalla PR A dell'issue #211: la
 * fattura e la garanzia sono di un ORDINE che puo' coprire altre attivita'.
 * Rimandare a ogni salvataggio il valore letto all'apertura del form
 * sovrascriverebbe quello che nel frattempo qualcuno ha registrato dalla scheda
 * di un'altra attivita' dello stesso ordine — e il pannello si salva anche per
 * cambiare una nota. Il backend tratta la chiave assente come «non toccare».
 *
 * Per le spunte il confronto con `iniziali` non basta: quando il numero porta a
 * un altro ordine il riquadro carica le spunte di quell'ordine, che differiscono
 * da `iniziali` senza che l'utente abbia deciso niente (review della PR #214).
 * Per questo decide `spunte_toccate`.
 */
export function payloadRiferimentiOdoo(
  v: ValoriRiferimentiOdoo,
  iniziali: ValoriRiferimentiOdoo
): Partial<PayloadRiferimentiOdoo> {
  const ora = normalizza(v);
  const prima = normalizza(iniziali);
  const out: Partial<PayloadRiferimentiOdoo> = {};
  if (ora.numero_oda !== prima.numero_oda) out.numero_oda = ora.numero_oda;
  if (ora.numero_odv !== prima.numero_odv) out.numero_odv = ora.numero_odv;
  for (const k of SPUNTE) {
    if (v.spunte_toccate?.[k]) (out as Record<string, unknown>)[k] = ora[k];
  }
  return out;
}

/** I nomi dei cinque campi (più `spunte_toccate`), per toglierli da un payload costruito con lo spread dei valori del form. */
const CHIAVI_RIFERIMENTI = [
  "numero_oda",
  "oda_fattura_ricevuta",
  "numero_odv",
  "odv_in_garanzia",
  "odv_fatturato",
  "spunte_toccate",
] as const;

/**
 * L'oggetto senza i cinque campi. Serve ai form che costruiscono il payload con
 * lo spread di tutti i valori (`...values`): senza toglierli, i campi NON
 * cambiati partirebbero lo stesso — nella forma del form, con `odv_in_garanzia`
 * come stringa — e `payloadRiferimentiOdoo` non potrebbe piu' mandare solo
 * quelli cambiati.
 */
export function senzaRiferimentiOdoo<T extends object>(obj: T): Omit<T, (typeof CHIAVI_RIFERIMENTI)[number]> {
  const copia = { ...obj } as Record<string, unknown>;
  for (const k of CHIAVI_RIFERIMENTI) delete copia[k];
  return copia as Omit<T, (typeof CHIAVI_RIFERIMENTI)[number]>;
}

/** Che cosa dice oggi il numero scritto nel campo. */
type StatoNumero =
  | { tipo: "vuoto" }
  /** E' il numero dell'ordine gia' collegato a questa attivita'. */
  | { tipo: "collegato"; ordine: OrdineCollegato }
  | { tipo: "cerco" }
  /** Un ordine con questo numero esiste gia': salvando, l'attivita' ci si collega. */
  | { tipo: "esistente"; ordine: OrdineTrovato }
  /** Nessun ordine con questo numero: salvando, se ne crea uno. */
  | { tipo: "nuovo" }
  | { tipo: "errore" };

/**
 * Lo stato del numero scritto: confrontato con l'ordine gia' collegato e, se e'
 * diverso, cercato fra gli ordini esistenti (con un attimo di attesa, per non
 * cercare a ogni tasto).
 */
function useStatoNumero(tipo: TipoOrdine, numero: string, collegato: OrdineCollegato | null | undefined): StatoNumero {
  const pulito = numero.trim();
  const [stato, setStato] = useState<StatoNumero>({ tipo: "vuoto" });
  const collegatoRef = useRef(collegato);
  collegatoRef.current = collegato;

  useEffect(() => {
    const giaCollegato = collegatoRef.current;
    if (pulito === "") {
      setStato({ tipo: "vuoto" });
      return;
    }
    if (giaCollegato && giaCollegato.numero === pulito) {
      setStato({ tipo: "collegato", ordine: giaCollegato });
      return;
    }
    setStato({ tipo: "cerco" });
    let annullato = false;
    const attesa = window.setTimeout(async () => {
      try {
        const ordine = await cercaOrdine(tipo, pulito);
        if (!annullato) setStato(ordine ? { tipo: "esistente", ordine } : { tipo: "nuovo" });
      } catch {
        if (!annullato) setStato({ tipo: "errore" });
      }
    }, 400);
    return () => {
      annullato = true;
      window.clearTimeout(attesa);
    };
  }, [tipo, pulito, collegato?.id, collegato?.numero]);

  return stato;
}

/** La riga sotto il numero: a quale ordine porta il salvataggio, detto prima. */
function AvvisoOrdine({ stato }: { stato: StatoNumero }) {
  const testo = (() => {
    switch (stato.tipo) {
      case "collegato":
        return stato.ordine.altre_attivita > 0
          ? `Questo ordine copre anche ${stato.ordine.altre_attivita} ${
              stato.ordine.altre_attivita === 1 ? "altra attività" : "altre attività"
            }: le spunte qui sotto valgono per tutte.`
          : null;
      case "esistente":
        return `Ordine già esistente, collegato a ${stato.ordine.attivita} attività: salvando, anche questa ci si collega. Le spunte qui sotto sono quelle dell'ordine e valgono per tutte.`;
      case "nuovo":
        return "Nessun ordine con questo numero: salvando, se ne crea uno nuovo.";
      case "errore":
        return "Non è stato possibile verificare se il numero esiste già.";
      default:
        return null;
    }
  })();
  if (!testo) return null;
  return <p className={`text-xs ${stato.tipo === "errore" ? "text-[#c2410c]" : "text-ink/60"}`}>{testo}</p>;
}

interface Props {
  valori: ValoriRiferimentiOdoo;
  onChange: (valori: ValoriRiferimentiOdoo) => void;
  disabled?: boolean;
  /**
   * Nome del modulo che ospita il riquadro, usato per gli `id` degli elementi:
   * la stessa pagina puo' avere due form aperti (elenco + pannello) e due
   * etichette che puntano allo stesso `id` ne attivano una sola.
   */
  idPrefisso: string;
  /**
   * Aggiunto dove il record ha GIA' un campo `in_garanzia` suo (interventi e
   * installazioni): li' due controlli "garanzia" nello stesso pannello si
   * confondono, e la riga di aiuto dice quale dei due si sta guardando.
   */
  distinguiDaInGaranzia?: boolean;
  /**
   * Gli ordini oggi collegati al record (`ordini_odoo` della risposta), per dire
   * quante altre attivita' coprono. Assente in creazione.
   */
  ordini?: OrdiniCollegati | null;
}

export function RiferimentiOdoo({ valori, onChange, disabled, idPrefisso, distinguiDaInGaranzia, ordini }: Props) {
  function cambia<K extends keyof ValoriRiferimentiOdoo>(chiave: K, valore: ValoriRiferimentiOdoo[K]) {
    const spunta = (SPUNTE as readonly string[]).includes(chiave);
    onChange({
      ...valori,
      [chiave]: valore,
      // Una spunta cambiata a mano e' una decisione dell'utente: parte nel
      // payload, e la ricerca del numero non la tocca piu'.
      ...(spunta ? { spunte_toccate: { ...valori.spunte_toccate, [chiave]: true } } : {}),
    });
  }

  const statoOda = useStatoNumero("oda", valori.numero_oda, ordini?.oda);
  const statoOdv = useStatoNumero("odv", valori.numero_odv, ordini?.odv);

  // Quando il numero porta a un ALTRO ordine, le spunte mostrate devono essere
  // quelle di quell'ordine, non quelle dell'ordine di prima: e' cio' che il
  // salvataggio collegherebbe. Su un ordine nuovo si ripartono da vuote, ma solo
  // quelle che venivano da un ordine. In tutti e due i casi una spunta TOCCATA
  // dall'utente non si cambia mai (review della PR #214): e' una sua decisione,
  // anche se presa mentre la ricerca era ancora in corso. «Venivano da un
  // ordine» e' un ricordo e non lo stato precedente: fra un ordine e il numero
  // nuovo lo stato passa per «cerco» a ogni tasto.
  const valoriRef = useRef(valori);
  valoriRef.current = valori;
  const dalOrdine = useRef({ oda: false, odv: false });

  /** Applica valori caricati da un ordine, saltando le spunte toccate a mano. */
  function applica(parziale: Partial<ValoriRiferimentiOdoo>) {
    const attuali = valoriRef.current;
    const daApplicare = (Object.keys(parziale) as (keyof ValoriRiferimentiOdoo)[]).filter(
      (k) => !attuali.spunte_toccate?.[k as SpuntaOrdine] && parziale[k] !== attuali[k]
    );
    if (daApplicare.length === 0) return;
    onChange({ ...attuali, ...Object.fromEntries(daApplicare.map((k) => [k, parziale[k]])) });
  }

  useEffect(() => {
    if (statoOda.tipo === "esistente" || statoOda.tipo === "collegato") {
      applica({ oda_fattura_ricevuta: statoOda.ordine.fattura_registrata });
      dalOrdine.current.oda = true;
    } else if (statoOda.tipo === "nuovo" && dalOrdine.current.oda) {
      applica({ oda_fattura_ricevuta: false });
      dalOrdine.current.oda = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statoOda]);

  useEffect(() => {
    if (statoOdv.tipo === "esistente" || statoOdv.tipo === "collegato") {
      const g = statoOdv.ordine.in_garanzia;
      applica({ odv_fatturato: statoOdv.ordine.fattura_registrata, odv_in_garanzia: g == null ? "" : String(g) });
      dalOrdine.current.odv = true;
    } else if (statoOdv.tipo === "nuovo" && dalOrdine.current.odv) {
      applica({ odv_fatturato: false, odv_in_garanzia: "" });
      dalOrdine.current.odv = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statoOdv]);

  // Senza numero non c'e' un ordine su cui registrare fattura o garanzia: il
  // backend le ignorerebbe, quindi il riquadro non le offre.
  const senzaOda = valori.numero_oda.trim() === "";
  const senzaOdv = valori.numero_odv.trim() === "";

  const selectId = `${idPrefisso}-odv-garanzia`;

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-dashed border-line p-3">
      <p className="text-sm font-bold uppercase tracking-wide text-ink/50">Riferimenti Odoo</p>

      {/* --- OdA: l'ordine verso il fornitore ---------------------------------- */}
      <div className="flex flex-col gap-2">
        <Input
          label="Numero OdA (ordine al fornitore)"
          value={valori.numero_oda}
          disabled={disabled}
          onChange={(e) => cambia("numero_oda", e.target.value)}
          placeholder="Es. PO00123"
          hint="Si incolla da Odoo: qui non c'è nessuna sincronizzazione."
        />
        <AvvisoOrdine stato={statoOda} />
        <label className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-black/20"
            checked={valori.oda_fattura_ricevuta}
            disabled={disabled || senzaOda}
            onChange={(e) => cambia("oda_fattura_ricevuta", e.target.checked)}
          />
          <span>
            <span className="font-medium">Fattura del fornitore ricevuta</span>
            <span className="text-ink/50">
              {" "}
              — finché è spenta, l'OdA compare fra le fatture da ricevere nel report Ordini.
            </span>
          </span>
        </label>
      </div>

      {/* --- OdV: l'ordine verso il cliente ------------------------------------ */}
      <div className="flex flex-col gap-2 border-t border-black/5 pt-3">
        <Input
          label="Numero OdV (ordine di vendita al cliente)"
          value={valori.numero_odv}
          disabled={disabled}
          onChange={(e) => cambia("numero_odv", e.target.value)}
          placeholder="Es. SO00777"
          hint="Senza questo numero l'attività non compare in nessuna sezione OdV del report."
        />
        <AvvisoOrdine stato={statoOdv} />

        <div className="flex flex-col gap-1.5">
          <label htmlFor={selectId} className="text-sm font-medium text-ink">
            OdV in garanzia
          </label>
          <select
            id={selectId}
            value={valori.odv_in_garanzia}
            disabled={disabled || senzaOdv}
            onChange={(e) => cambia("odv_in_garanzia", e.target.value)}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink disabled:opacity-60"
          >
            <option value="">Non specificato</option>
            <option value="true">Sì — non si fattura</option>
            <option value="false">No — da fatturare</option>
          </select>
          <p className="text-xs text-ink/50">
            «Non specificato» non è «No»: nel report Ordini finisce fra gli OdV <strong>da qualificare</strong>, cioè
            quelli su cui nessuno ha ancora deciso.
            {distinguiDaInGaranzia
              ? " Campo distinto da «in garanzia» qui sopra: quello riguarda il lavoro, questo l'ordine di vendita."
              : ""}
          </p>
        </div>

        <label className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-black/20"
            checked={valori.odv_fatturato}
            disabled={disabled || senzaOdv}
            onChange={(e) => cambia("odv_fatturato", e.target.checked)}
          />
          <span>
            <span className="font-medium">Fattura al cliente emessa</span>
            <span className="text-ink/50"> — toglie l'ordine dalla sezione «OdV da fatturare».</span>
          </span>
        </label>
      </div>
    </div>
  );
}
